/**
 * NZ Territory Map - lilac stained-glass version, status-driven pins.
 *
 * Each `area` row drives a single pin:
 *   { region, city, status: "open" | "pending" | "looking",
 *     waitlist_teaser, label, grower_name }
 *
 * Pins are placed by city name when known (CITY_COORDS), else by the
 * region centroid (REGION_COORDS). Status colours:
 *   open    - solid lilac brand, larger pin, badge with grower
 *   pending - gold, "Coming soon"
 *   looking - light/outlined, "Looking for growers"
 */
import React from "react";

// Region centroids on the 1254x1254 NZ map image (percentage based).
export const REGION_COORDS = {
  "Northland":            { x: 65, y: 13 },
  "Auckland":             { x: 67, y: 22 },
  "Waikato":              { x: 64, y: 28 },
  "Bay of Plenty":        { x: 73, y: 28 },
  "Gisborne":             { x: 80, y: 33 },
  "Taranaki":             { x: 55, y: 36 },
  "Manawatu-Whanganui":   { x: 64, y: 41 },
  "Hawke's Bay":          { x: 75, y: 39 },
  "Wellington":           { x: 64, y: 50 },
  "Tasman":               { x: 47, y: 56 },
  "Nelson":               { x: 49, y: 54 },
  "Marlborough":          { x: 55, y: 56 },
  "West Coast":           { x: 39, y: 67 },
  "Canterbury":           { x: 47, y: 73 },
  "Otago":                { x: 41, y: 84 },
  "Southland":            { x: 30, y: 90 },
};

// City pins (used when an area has a city set). Offset from region centroids.
export const CITY_COORDS = {
  "Wellington":   { x: 64, y: 50 },
  "New Plymouth": { x: 55, y: 36 },
  "Taupo":        { x: 68, y: 33 },
  "Queenstown":   { x: 33, y: 81 },
};

const STATUS_STYLES = {
  open: {
    dot: "w-5 h-5 bg-[var(--bloom-brand)] border-white shadow-lg shadow-[var(--bloom-brand)]/40",
    label: "bg-[var(--bloom-brand)] text-white",
  },
  pending: {
    dot: "w-4 h-4 bg-[#C9A14A] border-white shadow-md",
    label: "bg-[#C9A14A] text-white",
  },
  looking: {
    dot: "w-2.5 h-2.5 bg-white/85 border-[var(--bloom-ink-soft)]",
    label: "bg-white/90 text-[var(--bloom-ink-soft)] border border-[var(--bloom-border)]",
  },
};

function pinKey(a) {
  return `${a.region}__${a.city || ""}`;
}

function coordsFor(area) {
  if (area.city && CITY_COORDS[area.city]) return CITY_COORDS[area.city];
  return REGION_COORDS[area.region] || null;
}

export default function NZTerritoryMap({
  areas = null,
  pins = null,           // legacy: [{region, count}]
  activeKey = null,
  activeRegion = null,   // legacy
  onPinClick = null,
  onRegionClick = null,  // legacy
  className = "",
  showLegend = true,
  size = "default", // compact | default | large
}) {
  // Legacy adapter: convert old {region,count} pins into status-less areas.
  const effectiveAreas = areas || (pins || []).map((p) => ({
    id: `legacy-${p.region}`,
    region: p.region,
    city: null,
    status: p.count > 0 ? "open" : "looking",
    waitlist_teaser: 0,
    label: p.region,
  }));
  const effectiveActiveKey = activeKey || (activeRegion ? `${activeRegion}__` : null);
  const handleClick = (a) => {
    if (onPinClick) onPinClick(a);
    else if (onRegionClick) onRegionClick(a.region);
  };
  const heightClass =
    size === "compact" ? "max-h-[360px]" :
    size === "large"   ? "max-h-[720px]" :
                         "max-h-[560px]";

  return (
    <div className={`relative w-full ${className}`} data-testid="nz-territory-map">
      <div className={`relative mx-auto w-full ${heightClass} aspect-square`}>
        <img
          src="/maps/nz-territory.webp"
          alt="Heritage Bloom Club, New Zealand territory map"
          className="absolute inset-0 w-full h-full object-contain"
          draggable={false}
        />

        {effectiveAreas.map((a) => {
          const pos = coordsFor(a);
          if (!pos) return null;
          const styles = STATUS_STYLES[a.status] || STATUS_STYLES.looking;
          const key = pinKey(a);
          const isActive = effectiveActiveKey === key;
          const label = a.city || a.region;
          const teaser = a.waitlist_teaser || 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleClick(a)}
              className={`absolute group transition-all hover:z-20 ${(onPinClick || onRegionClick) ? "cursor-pointer" : "cursor-default"} ${isActive ? "z-30" : ""}`}
              style={{
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: "translate(-50%, -50%)",
              }}
              data-testid={`map-pin-${a.id || normalise(label)}`}
            >
              <span
                className={`block rounded-full border-2 transition-all group-hover:scale-125 ${styles.dot} ${isActive ? "ring-2 ring-[var(--bloom-ink)] ring-offset-2 ring-offset-white" : ""}`}
              />
              <span className={`absolute left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap font-serif text-[10px] tracking-[0.12em] uppercase px-1.5 py-0.5 rounded ${styles.label} ${a.status === "looking" ? "opacity-0 group-hover:opacity-100 transition" : ""}`}>
                {label}{teaser > 0 ? ` · ${teaser}` : ""}
              </span>
            </button>
          );
        })}
      </div>

      {showLegend && (
        <div className="mt-4 flex items-center justify-center gap-5 font-serif italic text-xs text-[var(--bloom-ink-muted)] flex-wrap" data-testid="map-legend">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-[var(--bloom-brand)] border-2 border-white shadow-sm" />
            Open, taking subscribers
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-[#C9A14A] border-2 border-white" />
            Coming soon
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-white border border-[var(--bloom-ink-soft)]" />
            Looking for a grower
          </span>
        </div>
      )}
    </div>
  );
}

function normalise(s) {
  return (s || "").toLowerCase().replace(/['']/g, "").replace(/[^a-z0-9]+/g, "");
}
